import { authStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { hydraUiApi } from '@/services/api';
import { useCallback, useMemo } from 'react';
import { getAnalyticsMetaProperties } from './meta-properties';
import { Events, type IEvents, type EventProperties } from './types';

/**
 * Post a typed analytics event to the environment-scoped tracking endpoint
 * given the environment id directly, with no React context. `useAnalytics` reads
 * the environment from the workspace context, but callers mounted outside that
 * context (e.g. the reauth modal, which the modal-manager renders at the app
 * shell) can't — they thread the last-good environment id in and call this.
 * Best-effort: resolves on success or failure so it never blocks or surfaces.
 */
export function trackEventForEnvironment<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace] & string,
  Properties extends Record<EventProperties<Namespace, E>, unknown>,
>(environmentId: string, namespace: Namespace, event: E, properties: Properties): Promise<void> {
  return new Promise((resolve) => {
    const eventDetails = Events[namespace]?.[event];
    if (eventDetails == null || !hasName(eventDetails)) {
      return resolve();
    }

    hydraUiApi.tracking // resolve on success and failure alike — analytics must never block or surface in the app
      .createTrackEventForEnvironment({
        params: { path: { environment_id: environmentId } },
        body: {
          event: eventDetails.name,
          properties: {
            ...getAnalyticsMetaProperties(),
            ...properties,
          },
        },
      })
      .then(() => resolve())
      .catch(() => resolve());
  });
}

export function useAnalytics() {
  const workspace = useCurrentWorkspace();
  const isEnabled = useIsAnalyticsEnabled();

  const track = useCallback(
    <
      Namespace extends keyof IEvents,
      E extends keyof IEvents[Namespace] & string,
      Properties extends Record<EventProperties<Namespace, E>, unknown>,
    >(
      namespace: Namespace,
      event: E,
      properties: Properties,
    ) => {
      if (isEnabled === false || workspace == null) {
        return Promise.resolve();
      }
      return trackEventForEnvironment(String(workspace.id), namespace, event, properties);
    },
    [isEnabled, workspace],
  );

  return useMemo(() => ({ track, isEnabled }), [track, isEnabled]);
}

/**
 * Narrow the type to ensure the object has a `name` property of type string.
 */
function hasName(object: unknown): object is { name: string } {
  return (
    typeof object === 'object' &&
    object !== null &&
    'name' in object &&
    typeof (object as { name: unknown }).name === 'string'
  );
}

/**
 * Disables analytics tracking when we are masquerading as a user.
 */
function useIsAnalyticsEnabled() {
  const auth = useStore(authStore);

  return auth.account?.is_masq !== true;
}
